import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import BirthDetailsForm from './components/BirthDetailsForm';
import OnboardingFlow from './components/OnboardingFlow';
import ChatInterface from './components/ChatInterface';
import ProfileSwitcher from './components/ProfileSwitcher';
import Feedback from './components/Feedback';
import {
  loadProfiles,
  writeStore,
  setActiveProfileId,
  blankProfile,
  deriveProfileName,
} from './services/profiles';

function App() {
  // One-time load: migrates legacy single-profile storage if present.
  const initial = useMemo(() => loadProfiles(), []);
  const [profiles, setProfiles] = useState(initial.profiles);
  const [showFeedback, setShowFeedback] = useState(false);
  const [activeId, setActiveId] = useState(initial.activeId);

  // Persist the whole store on any change. Chat histories are the bulk of
  // this, and they only change a couple of times per minute.
  useEffect(() => {
    writeStore({ profiles });
  }, [profiles]);

  useEffect(() => {
    setActiveProfileId(activeId);
  }, [activeId]);

  const activeProfile =
    profiles.find((p) => p.id === activeId) || profiles[0] || null;

  /** Merge a patch into one profile without disturbing the others. */
  const updateProfile = useCallback((id, patch) => {
    setProfiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
    );
  }, []);

  const handleBirthDetailsSubmit = useCallback(
    (data) => {
      if (!activeProfile) return;
      updateProfile(activeProfile.id, {
        birthData: data,
        step: 'onboarding',
        // The name typed on the form wins — editing details should update it.
        // Fall back to any existing name so a blank submit can't erase one.
        name: (data.name || '').trim() || activeProfile.name || '',
      });
    },
    [activeProfile, updateProfile]
  );

  const handleChartCalculated = useCallback(
    (chartData) => {
      if (!activeProfile) return;
      updateProfile(activeProfile.id, { chartData });
    },
    [activeProfile, updateProfile]
  );

  const handleOnboardingComplete = useCallback(
    (interests) => {
      if (!activeProfile) return;
      updateProfile(activeProfile.id, { interests, step: 'chat' });
    },
    [activeProfile, updateProfile]
  );

  const handleMessagesChange = useCallback(
    (messages) => {
      if (!activeProfile) return;
      updateProfile(activeProfile.id, { messages });
    },
    [activeProfile, updateProfile]
  );

  const handleAddProfile = useCallback(() => {
    const fresh = blankProfile();
    setProfiles((prev) => [...prev, fresh]);
    setActiveId(fresh.id);
  }, []);

  const handleRenameProfile = useCallback(
    (id, name) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      updateProfile(id, { name: trimmed });
    },
    [updateProfile]
  );

  const handleDeleteProfile = useCallback(
    (id) => {
      setProfiles((prev) => {
        const remaining = prev.filter((p) => p.id !== id);
        // Never leave the app with zero profiles — the flow has nowhere to go.
        const next = remaining.length > 0 ? remaining : [blankProfile()];
        setActiveId((currentActive) =>
          currentActive === id ? next[0].id : currentActive
        );
        return next;
      });
    },
    []
  );

  if (!activeProfile) return null;

  const step = activeProfile.step || 'birth-details';

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-inner">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="1.1" opacity="0.45" />
                <circle cx="16" cy="16" r="5.2" fill="currentColor" opacity="0.9" />
                <circle cx="16" cy="3" r="1.8" fill="currentColor" />
                <circle cx="27" cy="21" r="1.3" fill="currentColor" opacity="0.75" />
                <circle cx="6" cy="23" r="1" fill="currentColor" opacity="0.6" />
              </svg>
            </span>
            <span className="brand-text">
              <h1 className="logo">Astrobot</h1>
              <p className="tagline">Vedic astrology, precisely calculated</p>
            </span>
          </div>

          <ProfileSwitcher
            profiles={profiles}
            activeId={activeProfile.id}
            onSelect={setActiveId}
            onAdd={handleAddProfile}
            onRename={handleRenameProfile}
            onDelete={handleDeleteProfile}
            onFeedback={() => setShowFeedback(true)}
          />
        </div>
      </header>

      <main className="App-main">
        {/* Keying on profile id forces a clean remount when switching charts,
            so no state leaks between profiles. */}
        {step === 'birth-details' && (
          <BirthDetailsForm
            key={activeProfile.id}
            profileId={activeProfile.id}
            initialData={activeProfile.birthData}
            onSubmit={handleBirthDetailsSubmit}
          />
        )}

        {step === 'onboarding' && (
          <OnboardingFlow
            key={activeProfile.id}
            birthData={activeProfile.birthData}
            existingChart={activeProfile.chartData}
            onComplete={handleOnboardingComplete}
            onChartCalculated={handleChartCalculated}
            onBack={() =>
              updateProfile(activeProfile.id, { step: 'birth-details' })
            }
          />
        )}

        {step === 'chat' && (
          <ChatInterface
            key={activeProfile.id}
            birthData={activeProfile.birthData}
            chartData={activeProfile.chartData}
            userInterests={activeProfile.interests}
            messages={activeProfile.messages}
            onMessagesChange={handleMessagesChange}
            profileName={deriveProfileName(activeProfile)}
            onEditDetails={() =>
              updateProfile(activeProfile.id, { step: 'birth-details' })
            }
          />
        )}
      </main>

      {showFeedback && (
        <Feedback
          onClose={() => setShowFeedback(false)}
          profileName={activeProfile ? deriveProfileName(activeProfile) : ''}
        />
      )}
    </div>
  );
}

export default App;
